import React from "react";
import SimpleSelect from "@/main/react/component/SimpleSelect";
import { Languages } from "lucide-react";
import { Localizer } from "@/main/react/Types";
import { UserDiscussionLanguageScope } from "@/main/react/page/profile-page/ProfilePageQueries";

/**
 * Small "All languages / Your languages" selector for a profile or dashboard discussion list.
 *
 * "Your languages" is the viewer's whole reading-set — the same set the home feed matches on, not
 * the UI locale alone (changed 2026-07-29; see
 * designs/01-now/2026-07-29-your-languages-reading-set-scope-design.md).
 *
 * The default is caller-controlled: another user's profile (`UserPage`) defaults to the viewer's
 * own languages ("mine"), while the own dashboard (`MyDashboardPage`) defaults to ALL (you wrote
 * those posts, so language should not hide any). Either way this control lets the viewer switch.
 * Renders bare (no row wrapper) so callers can place it inline alongside other header controls.
 */
export function DiscussionLanguageScopeSelect({
    value,
    onChange,
    localizer,
}: {
    value: UserDiscussionLanguageScope;
    onChange: (value: UserDiscussionLanguageScope) => void;
    localizer: Localizer;
}) {
    const options = React.useMemo(
        () => [
            { key: "all", value: "all", label: localizer("profile.language-scope.all"), icon: Languages },
            { key: "mine", value: "mine", label: localizer("profile.language-scope.mine") },
        ],
        [localizer],
    );

    /**
     * `DISCOVERY-83` — the accessible name CONTAINS the visible string.
     *
     * The trigger's only visible text is its selected value ("Your languages" / "All languages"), and
     * an `aria-label` overrides an element's contents entirely — so the name was "Filter posts by
     * language" and the two shared no word. A speech-input user reading the screen and saying "click
     * Your languages" had nothing to match, which is SC 2.5.3's whole subject. Speech control is a
     * phone input method and on the tag hub this is the page's only body control, so the cost falls
     * squarely on the members least able to route around it.
     *
     * Composed from the two keys that already ship in all 25 catalogs rather than minting a third, and
     * purpose-first because that is also the better screen-reader reading ("Filter posts by language:
     * Your languages" announces what the control does before what it currently says). Containment, not
     * prefix, is what the success criterion requires.
     *
     * Composed HERE rather than in `SimpleSelect`: this is the one caller whose visible string is the
     * value and whose `ariaLabel` is unrelated to it, and the shared select serves ~24 other call sites
     * (mostly forms) that have real labels beside them. See the row for why the general fix — pairing
     * every trigger with a visually-hidden label through `aria-labelledby` — is a wider change than
     * this row asks for.
     */
    const selectedLabel = value === "mine"
        ? localizer("profile.language-scope.mine")
        : localizer("profile.language-scope.all");
    const ariaLabel = `${localizer("profile.language-scope.aria")}: ${selectedLabel}`;

    return (
        <SimpleSelect
            size="sm"
            value={value}
            onValueChange={(next) => onChange(next === "mine" ? "mine" : "all")}
            options={options}
            ariaLabel={ariaLabel}
            dataPlaywrightId="discussion-language-scope-select"
        />
    );
}

export default DiscussionLanguageScopeSelect;
